import { useTranslation } from 'react-i18next';

import { LOCALES, type Locale } from '@h3eromap/i18n';
import { setLocale } from '@/lib/i18n';
import { useAuth } from '@/app/auth-context';
import { updateMyLanguage } from '@/lib/api-client';

export function LanguageSwitcher(): JSX.Element {
  const { t, i18n } = useTranslation();
  const { user, accessToken, setUserLanguage } = useAuth();
  const current = (i18n.resolvedLanguage ?? 'de') as Locale;

  return (
    <label className="flex items-center gap-s2 text-sm text-gray-2">
      <span className="sr-only">{t('common.labels.language')}</span>
      <select
        aria-label={t('common.labels.language')}
        className="bg-bone-2 border border-bone-3 rounded-sm px-s2 py-s1 focus:outline-none focus:ring-1 focus:ring-flame text-ink"
        value={current}
        onChange={(event) => {
          const next = event.target.value as Locale;
          void setLocale(next);
          // Persist to the server so reminder / transactional emails go out in
          // the chosen language. Best-effort: the UI switches regardless.
          // (`de`/`en` only — the API enum carries no other locales.)
          if (user && (next === 'de' || next === 'en')) {
            // Keep the in-memory + persisted session language in step so a
            // reload (which re-applies the saved session language) doesn't
            // revert the page to the old locale.
            setUserLanguage(next);
            void updateMyLanguage(accessToken ?? '', next).catch(() => {
              /* silent — the UI language already changed locally */
            });
          }
        }}
      >
        {LOCALES.map((code) => (
          <option key={code} value={code}>
            {t(`common.languages.${code}`)}
          </option>
        ))}
      </select>
    </label>
  );
}
